import { useState, useEffect } from 'react'
import { Card } from '@/components/ui/Card'
import { Badge } from '@/components/ui/Badge'
import { Button } from '@/components/ui/Button'
import { cn } from '@/lib/utils'
import { BookOpen, Users, Tag, FileText, PlusCircle, Archive, Link2 } from 'lucide-react'

type ItemKind = 'segment' | 'theme' | 'quote' | 'validation_archetype' | 'rule'

interface LibraryItem {
  item_id: string
  kind: ItemKind
  name: string
  description?: string
  tags?: string[]
  reuse_count: number
  last_used_at?: number
  source_project_id?: string
  archived: boolean
}

const KIND_CONFIG: Record<ItemKind, { label: string; Icon: typeof BookOpen; color: string }> = {
  segment: { label: 'Segment', Icon: Users, color: 'text-echo-indigo' },
  theme: { label: 'Theme', Icon: Tag, color: 'text-human-teal' },
  quote: { label: 'Quote', Icon: FileText, color: 'text-signal-cyan' },
  validation_archetype: { label: 'Validation Archetype', Icon: Link2, color: 'text-uncertainty-amber' },
  rule: { label: 'Rule', Icon: BookOpen, color: 'text-validation-green' },
}

const DEMO_ITEMS: LibraryItem[] = [
  { item_id: 'lib_001', kind: 'segment', name: 'High-Value Auto Policyholder', description: 'Age 45–65, multi-vehicle, >5 years tenure, no claims last 3 years.', tags: ['insurance', 'auto'], reuse_count: 12, last_used_at: Date.now() - 3 * 24 * 60 * 60 * 1000, source_project_id: 'proj_ins_001', archived: false },
  { item_id: 'lib_002', kind: 'segment', name: 'Lapsed Premium Segment', description: 'Policyholders who let coverage lapse and returned within 6 months.', tags: ['insurance', 're-engagement'], reuse_count: 7, source_project_id: 'proj_ins_002', archived: false },
  { item_id: 'lib_003', kind: 'theme', name: 'Price Transparency Signal', description: '"Hidden fees" and "surprise charges" themes cluster across insurance churn data.', tags: ['churn', 'pricing'], reuse_count: 5, archived: false },
  { item_id: 'lib_004', kind: 'quote', name: '"I didn\'t understand my deductible"', description: 'Recurring comprehension failure across 4 studies. High signal for plain-language intervention.', tags: ['comprehension', 'insurance'], reuse_count: 9, archived: false },
  { item_id: 'lib_005', kind: 'validation_archetype', name: 'High-Churn Risk — Short Survey', description: '8-question validated archetype for at-risk segments. Wilson 0.84 on last 3 runs.', tags: ['churn', 'validated'], reuse_count: 3, archived: false },
]

export function LibraryPage() {
  const [kindFilter, setKindFilter] = useState<ItemKind | 'all'>('all')
  const [showPromoteModal, setShowPromoteModal] = useState(false)
  const [attached, setAttached] = useState<Record<string, boolean>>({})
  const [items, setItems] = useState<LibraryItem[]>([])
  const [loading, setLoading] = useState(true)
  const [isAuthenticated, setIsAuthenticated] = useState(false)

  useEffect(() => {
    const token = localStorage.getItem('echo_session')
    if (!token) {
      setIsAuthenticated(false)
      setItems(DEMO_ITEMS)
      setLoading(false)
      return
    }
    setIsAuthenticated(true)
    fetch('/api/v1/library', { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() as Promise<{ items: LibraryItem[] }> : ({ items: [] as LibraryItem[] }))
      .then(d => setItems(d.items ?? []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false))
  }, [])

  const displayed = items.filter(item =>
    !item.archived && (kindFilter === 'all' || item.kind === kindFilter)
  )

  return (
    <div className="p-6 space-y-6 max-w-5xl pb-20 md:pb-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-medium text-deep-ink" style={{ letterSpacing: '-0.02em' }}>Research Library</h1>
          <p className="text-sm text-slate-500 mt-1">Segments, themes, quotes, and validated archetypes reusable across projects.</p>
        </div>
        <Button variant="primary" size="sm" onClick={() => setShowPromoteModal(true)}>
          <PlusCircle className="w-4 h-4 mr-1.5" />Add to library
        </Button>
      </div>

      {!isAuthenticated && (
        <div className="flex items-center gap-1.5">
          <Badge variant="demo" size="sm">Demo data</Badge>
          <span className="text-xs text-slate-400">Sign in to see your real library</span>
        </div>
      )}

      {/* Kind tabs */}
      <div className="flex gap-2 flex-wrap">
        {(['all', 'segment', 'theme', 'quote', 'validation_archetype', 'rule'] as const).map(k => (
          <button
            key={k}
            className={cn(
              'text-sm px-3 py-1.5 rounded-lg border transition-colors',
              kindFilter === k
                ? 'bg-echo-indigo text-white border-echo-indigo'
                : 'border-border-gray text-slate-500 hover:border-slate-300'
            )}
            onClick={() => setKindFilter(k)}
          >
            {k === 'all' ? 'All' : KIND_CONFIG[k as ItemKind]?.label ?? k}
          </button>
        ))}
      </div>

      {loading && (
        <Card className="text-center py-8">
          <p className="text-sm text-slate-400">Loading library…</p>
        </Card>
      )}

      {!loading && isAuthenticated && displayed.length === 0 && (
        <Card className="text-center py-8 space-y-2">
          <p className="text-sm font-medium text-deep-ink">Your library is empty</p>
          <p className="text-xs text-slate-500">Segments, themes, and validated archetypes you save from projects will appear here.</p>
          <Button variant="primary" size="sm" className="mt-3" onClick={() => setShowPromoteModal(true)}>Add first item</Button>
        </Card>
      )}

      {/* Cold start suggestions — only for unauthenticated demo */}
      {!isAuthenticated && (
        <Card className="bg-echo-indigo/5 border-echo-indigo/20">
          <p className="text-xs text-echo-indigo font-medium mb-0.5">Cold start suggestions</p>
          <p className="text-xs text-slate-600">Starting a new insurance project? These 4 items from prior work may be relevant — <button className="underline text-human-teal">attach to new project</button>.</p>
        </Card>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {displayed.map(item => {
          const conf = KIND_CONFIG[item.kind]
          const Icon = conf.Icon
          const isAttached = attached[item.item_id]
          const age = item.last_used_at
            ? `Last used ${Math.round((Date.now() - item.last_used_at) / (24 * 60 * 60 * 1000))}d ago`
            : 'Never used'

          return (
            <Card key={item.item_id}>
              <div className="flex items-start gap-3">
                <div className="p-2 bg-soft-panel rounded-lg shrink-0">
                  <Icon className={cn('w-4 h-4', conf.color)} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2 mb-1">
                    <div>
                      <p className="text-sm font-medium text-deep-ink">{item.name}</p>
                      <p className="text-xs text-slate-500 mt-0.5">{item.description}</p>
                    </div>
                    <Badge variant="default" size="sm">{conf.label}</Badge>
                  </div>
                  <div className="flex flex-wrap gap-1 mt-2">
                    {item.tags?.map(t => (
                      <span key={t} className="text-xs bg-soft-panel text-slate-500 px-2 py-0.5 rounded-full">{t}</span>
                    ))}
                  </div>
                  <div className="flex items-center justify-between mt-3 pt-3 border-t border-border-gray">
                    <div className="text-xs text-slate-400">
                      <span className="font-medium text-deep-ink">{item.reuse_count}×</span> reused · {age}
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant={isAttached ? 'ghost' : 'primary'}
                        size="sm"
                        onClick={() => setAttached(prev => ({ ...prev, [item.item_id]: true }))}
                        disabled={isAttached}
                      >
                        {isAttached ? 'Attached' : 'Attach to project'}
                      </Button>
                      <button className="p-1.5 text-slate-400 hover:text-slate-600 rounded-lg hover:bg-soft-panel">
                        <Archive className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </Card>
          )
        })}
      </div>

      {displayed.length === 0 && (
        <Card className="text-center py-12">
          <BookOpen className="w-8 h-8 text-slate-300 mx-auto mb-3" />
          <p className="text-sm font-medium text-deep-ink">Library is empty</p>
          <p className="text-xs text-slate-500 mt-1">Promote segments, themes, or quotes from any project to build your library.</p>
        </Card>
      )}
    </div>
  )
}
